import React from "react";
import { Tooltip } from "@mui/material";
import { MdRestore, MdClose, MdAutorenew } from "react-icons/md";

const AssetCard = ({
  icon: Icon,
  title,
  description,
  isGenerated,
  isGenerating,
  progress,
  onGenerate,
  onRegenerate,
  onView,
  viewButtonText = "View",
  generateButtonText = "Generate",
  isLocked = false,
  tooltipText = "",
  isLoading = false,
  isReviewContent = false,
  comingSoon = false,
  showDualButtons = false, // Enables Generate button alongside View when generated
  showRegenerateButton = false, // Enables an outlined Regenerate button alongside View when generated
  isFailed = false,
  showPreviousAssetsButton = false,
  onPreviousAssets,
  onCancel,
  isQueued = false,
}) => {
  // Apply locked styling if isLocked OR comingSoon
  const isDisabled = isLocked || comingSoon;

  return (
    <div className={`card ${isDisabled ? "card-locked" : ""}`} style={{ position: 'relative' }}>
      {comingSoon && (
        <div style={{
          position: 'absolute',
          top: '-10px',
          right: '-10px',
          background: 'linear-gradient(45deg, #1E4DE3, #5c7fed)',
          color: 'white',
          padding: '0.3rem 0.6rem',
          borderRadius: '12px',
          fontSize: '0.7rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          zIndex: 1,
        }}>
          Coming Soon
        </div>
      )}
      <Icon className={`card-icon ${isGenerated ? "generated" : ""}`} />
      <div className="card-title">{title}</div>
      <div className="card-description">{description}</div>

      {isLoading ? (
        <div key="state-loading" style={{ padding: '10px', color: '#666', fontSize: '14px' }}>
          Loading...
        </div>
      ) : isDisabled ? (
        <Tooltip key="state-disabled" title={tooltipText || "This feature is currently unavailable"} arrow placement="top">
          <span>
            <button className="generate-button locked-button" disabled>
              {generateButtonText}
            </button>
          </span>
        </Tooltip>

      ) : isQueued ? (
        // Extraction (section-selection) or queued phase is actively running. Show a disabled Generate button.
        <div key="state-queued" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: 'auto' }}>
          <button className="generate-button" onClick={onGenerate} style={{ width: '100%', margin: 0 }}>
            {generateButtonText}
          </button>
          {showPreviousAssetsButton && (
            <button
              className="previous-assets-link"
              onClick={onPreviousAssets}
            >
              <MdRestore style={{ fontSize: '16px' }} />
              Previous Assets
            </button>
          )}
        </div>
      ) : isGenerating && progress >= 0 ? (
        <div key="state-generating" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
            <div className="progress-container" style={{ margin: 0, flex: 1 }}>
              <div className="progress-label">Progress: {progress}%</div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
            {onCancel && (
              <button
                onClick={onCancel}
                title="Cancel Generation"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'none',
                  border: '1px solid #d3d3d3',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px',
                  height: '32px',
                  width: '32px',
                  marginTop: '16px' // Aligning with the progress bar visually
                }}
              >
                <MdClose style={{ fontSize: '18px' }} />
              </button>
            )}
          </div>
          {showPreviousAssetsButton && (
            <button
              className="previous-assets-link"
              onClick={onPreviousAssets}
            >
              <MdRestore style={{ fontSize: '16px' }} />
              Previous Assets
            </button>
          )}
        </div>
      ) : isGenerated ? (
        showDualButtons || showRegenerateButton ? (
          showPreviousAssetsButton ? (
            <div key="state-generated-dual-prev" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: 'auto' }}>
              <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                {showRegenerateButton ? (
                  <button className="generate-button" onClick={onRegenerate || onGenerate} style={{ flex: 1, margin: 0, backgroundColor: 'transparent', border: '1px solid #1e4de3', color: '#1e4de3', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                    <MdAutorenew style={{ fontSize: '18px' }} />
                    Regenerate
                  </button>
                ) : (
                  <button className="generate-button" onClick={onGenerate} style={{ flex: 1, margin: 0, whiteSpace: 'nowrap' }}>
                    {generateButtonText}
                  </button>
                )}
                {isFailed ? (
                  <button
                    className="generate-button generated view-content"
                    style={{ flex: 1, margin: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onClick={onRegenerate || onGenerate}
                  >
                    Retry
                  </button>
                ) : (
                  <button className={`generate-button generated ${isReviewContent ? 'review-content' : 'view-content'}`} onClick={onView} style={{ flex: 1, margin: 0, whiteSpace: 'nowrap' }}>
                    {viewButtonText}
                  </button>
                )}
              </div>
              <button
                className="previous-assets-link"
                onClick={onPreviousAssets}
              >
                <MdRestore style={{ fontSize: '16px' }} />
                Previous Assets
              </button>
            </div>
          ) : (
            <div key="state-generated-dual" style={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'center' }}>
              {showRegenerateButton ? (
                <button className="generate-button" onClick={onRegenerate || onGenerate} style={{ flex: 1, margin: 0, backgroundColor: 'transparent', border: '1px solid #1e4de3', color: '#1e4de3', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                  <MdAutorenew style={{ fontSize: '18px' }} />
                  Regenerate
                </button>
              ) : (
                <button className="generate-button" onClick={onGenerate} style={{ flex: 1, margin: 0 }}>
                  {generateButtonText}
                </button>
              )}
              {isFailed ? (
                <button
                  className="generate-button generated view-content"
                  style={{ flex: 1, margin: 0, cursor: 'pointer' }}
                  onClick={onRegenerate || onGenerate}
                >
                  Retry
                </button>
              ) : (
                <button className={`generate-button generated ${isReviewContent ? 'review-content' : 'view-content'}`} onClick={onView} style={{ flex: 1, margin: 0 }}>
                  {viewButtonText}
                </button>
              )}
            </div>
          )
        ) : (
          showPreviousAssetsButton ? (
            <div key="state-generated-single-prev" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: 'auto' }}>
              {isFailed ? (
                <button
                  className="generate-button generated view-content"
                  style={{ width: '100%', margin: 0, cursor: 'pointer' }}
                  onClick={onRegenerate || onGenerate}
                >
                  Retry
                </button>
              ) : (
                <button className={`generate-button generated ${isReviewContent ? 'review-content' : 'view-content'}`} onClick={onView} style={{ width: '100%', margin: 0 }}>
                  {viewButtonText}
                </button>
              )}
              <button
                className="previous-assets-link"
                onClick={onPreviousAssets}
              >
                <MdRestore style={{ fontSize: '16px' }} />
                Previous Assets
              </button>
            </div>
          ) : (
            isFailed ? (
              <button
                className="generate-button generated view-content"
                style={{ cursor: 'pointer' }}
                onClick={onRegenerate || onGenerate}
              >
                Retry
              </button>
            ) : (
              <button className={`generate-button generated ${isReviewContent ? 'review-content' : 'view-content'}`} onClick={onView}>
                {viewButtonText}
              </button>
            )
          )
        )
      ) : tooltipText ? (
        showPreviousAssetsButton ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: 'auto' }}>
            <Tooltip title={tooltipText} arrow placement="top">
              <span style={{ width: '100%', display: 'flex' }}>
                <button className="generate-button" onClick={onGenerate} style={{ flex: 1, width: '100%', margin: 0 }}>
                  {generateButtonText}
                </button>
              </span>
            </Tooltip>
            <button
              className="previous-assets-link"
              onClick={onPreviousAssets}
            >
              <MdRestore style={{ fontSize: '16px' }} />
              Previous Assets
            </button>
          </div>
        ) : (
          <Tooltip title={tooltipText} arrow placement="top">
            <span>
              <button className="generate-button" onClick={onGenerate}>
                {generateButtonText}
              </button>
            </span>
          </Tooltip>
        )
      ) : (
        showPreviousAssetsButton ? (
          <div key="state-default-prev" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: 'auto' }}>
            <button className="generate-button" onClick={onGenerate} style={{ width: '100%', margin: 0 }}>
              {generateButtonText}
            </button>
            <button
              className="previous-assets-link"
              onClick={onPreviousAssets}
            >
              <MdRestore style={{ fontSize: '16px' }} />
              Previous Assets
            </button>
          </div>
        ) : (
          <button className="generate-button" onClick={onGenerate}>
            {generateButtonText}
          </button>
        )
      )}
    </div>
  );
};

export default AssetCard;
